import { useEffect, useState } from "react";
import { API_URL } from "@/config/api";
import { STORAGE_KEYS } from "@/config/storageKeys";
import { parseCachedCount, countFromModels } from "./modelCountUtils";
import axios from "axios";

// Cache the count in sessionStorage so each page navigation doesn't
// re-hit /api/models. The catalog changes rarely; per-tab freshness
// is fine.
const CACHE_KEY = STORAGE_KEYS.MODEL_COUNT_CACHE;

// Read + validate the cached counts. parseCachedCount does the JSON parse and
// shape validation (pure, tested); the try/catch here only guards the
// sessionStorage access itself (can throw in private mode).
const readCache = () => {
  try {
    return parseCachedCount(sessionStorage.getItem(CACHE_KEY));
  } catch {
    return null;
  }
};

const toCache = (value) => {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(value));
  } catch {
    // Quota / private mode — ignore. The hook still works without cache.
  }
};

/**
 * Live model + provider counts from `/api/models`.
 *
 * Used wherever the marketing copy claims "N modèles IA" so the number
 * stays in sync with the actual catalog instead of drifting against
 * hardcoded "12+" / "25+" strings that age badly (cf. user report
 * 2026-05-21: pricing said 12, dashboard said 29).
 *
 * Excludes the "auto" pseudo-model (it's a routing meta-entry, not a
 * concrete model) — same logic as the dashboard's model-count badge.
 *
 * @param {{ models: number, providers: number } | null} fallback
 *   Static counts to use until the API responds (SEO crawler also
 *   sees this). Pass the known-true counts at PR time so the page
 *   never shows "?" or 0 to a real user.
 * @returns {{ models: number, providers: number, loaded: boolean }}
 */
export const useModelCount = (fallback = null) => {
  const cached = readCache();
  const initial = cached || fallback || { models: 0, providers: 0 };
  const [state, setState] = useState({ ...initial, loaded: !!cached });

  useEffect(() => {
    let active = true;
    axios
      .get(`${API_URL}/api/models`)
      .then((res) => {
        if (!active) return;
        const value = countFromModels(res.data);
        if (!value) return; // non-array response → keep the fallback
        setState({ ...value, loaded: true });
        toCache(value);
      })
      .catch(() => {
        // /api/models is unauthenticated and stable in prod. If the call
        // fails (rare — Render cold start, offline), keep the fallback
        // rather than render "0 modèles" which would scream broken.
      });
    return () => {
      active = false;
    };
  }, []);

  return state;
};

export default useModelCount;
